/* eslint-disable no-param-reassign */
import { replaceSchemaTypes } from '@sapappgyver/appgyver-schemas';
import { omit } from 'lodash';
import { weakMapMemoize } from '@reduxjs/toolkit';
import { ComponentPropertyDefinition, NodeRedFlowSubflow } from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { Schema } from '@sapappgyver/appgyver-descriptors/lib/common/schemas';

type InputSchema = Schema | ComponentPropertyDefinition | NonNullable<NodeRedFlowSubflow['arguments']>[number];

/**
 * Ensures that the given legacy schema is represented in the recommended JSON schema format:
 * https://github.com/AppGyver/@sapappgyver/appgyver-formulas/blob/master/docs/SCHEMA.md
 * @param {object|null} schema legacy schema object
 * @returns {import('@sapappgyver/appgyver-schemas').Schema & import('@sapappgyver/appgyver-descriptors/lib/dolan/v4').Schema}
 */
function doMigrateSchema(schema: ComponentPropertyDefinition): Schema & ComponentPropertyDefinition;
function doMigrateSchema(schema: InputSchema): Schema;
function doMigrateSchema(schema: InputSchema | null | undefined): Schema | null | undefined;
function doMigrateSchema(schema: InputSchema | null | undefined): Schema | null | undefined {
  if (!schema) {
    return schema;
  }
  // Convert 'tooltip' to description
  if ('tooltip' in schema) {
    const { description, tooltip } = schema;
    if (!description && typeof tooltip === 'string') {
      schema = { ...schema, description: tooltip };
    }
    schema = omit(schema, 'tooltip');
  }
  if (schema.type === 'options') {
    const { metadata } = schema;
    const options = typeof metadata === 'object' && metadata != null && 'options' in metadata ? metadata.options : null;
    // Convert legacy 'options' format to a JSON schema enum format
    schema = { ...schema, type: 'string' };
    if (Array.isArray(options)) {
      schema.enum = options;
    }
  }
  if ('metadata' in schema) {
    schema = omit(schema, 'metadata');
  }
  if ('anyOf' in schema && !schema.anyOf) {
    schema = omit(schema, 'anyOf');
  }
  if ('enum' in schema && !schema.enum) {
    schema = omit(schema, 'enum');
  }
  if ('examples' in schema && !schema.examples?.length) {
    schema = omit(schema, 'examples');
  }
  if ('format' in schema && schema.format == null) {
    schema = omit(schema, 'format');
  }
  if ('items' in schema && schema.items == null) {
    schema = omit(schema, 'items');
  }
  if ('properties' in schema && schema.properties == null) {
    schema = omit(schema, 'properties');
  }
  return replaceSchemaTypes(schema as Schema, {
    image: { type: 'string', format: 'web-url', uiType: 'image-picker' },
    icon: { type: 'string', format: 'icon' },
  });
}

const migrateSchema = weakMapMemoize(doMigrateSchema);

export default migrateSchema;
